import { useTranslation } from 'react-i18next';
import { Check, Clock } from 'lucide-react';
import { Badge, cx } from '@/components/ui';
import {
  offerReward,
  offerRule,
  offerTimeLeft,
  offerTimeLeftLabel,
  rewardSize,
  type CampaignOffer,
} from './campaignOfferCopy';
import { OfferStopwatch } from './OfferArt';
import './campaignOffer.css';

/** Tags under an offer: the one-time slot or Claimed, and the time left (red under a day). */
export function OfferMarks({ offer, now }: { offer: CampaignOffer; now: number }) {
  const { t, i18n } = useTranslation();
  if (offer.used) {
    return (
      <Badge tone="success" dot={false} size="sm">
        <Check className="size-3.5" strokeWidth={2.5} aria-hidden />
        {t('campaignBonus.claimed', 'Claimed')}
      </Badge>
    );
  }
  const left = offerTimeLeft(offer.endsAt, now);
  const leftLabel = offerTimeLeftLabel(left, t, i18n.language);
  return (
    <>
      {offer.application === 'first_deposit' ? (
        <span className="offer-tag offer-tag--once">{t('campaignBonus.oneTime', '1 time')}</span>
      ) : null}
      {leftLabel ? (
        <span className={cx('offer-tag', left.urgent ? 'offer-tag--urgent' : 'offer-tag--time')}>
          {left.urgent ? <OfferStopwatch className="size-3.5" /> : <Clock className="size-3.5" aria-hidden />}
          {leftLabel}
        </span>
      ) : null}
    </>
  );
}

export interface OfferTicketProps {
  offer: CampaignOffer;
  now: number;
}

/** Coupon ticket: red stub with the reward, tear line, then the offer's message, rule and tags. */
export default function OfferTicket({ offer, now }: OfferTicketProps) {
  const { t } = useTranslation();
  const reward = offerReward(offer, t);
  return (
    <article
      className={cx('offer-ticket', offer.used && 'is-used')}
      data-testid="campaign-offer-ticket"
    >
      <span className="offer-ticket__notch offer-ticket__notch--top" aria-hidden />
      <span className="offer-ticket__notch offer-ticket__notch--bottom" aria-hidden />
      <div className="offer-ticket__stub">
        <span className={cx('offer-reward', `offer-reward--${rewardSize(reward.value)}`)}>{reward.value}</span>
        <span className="offer-ticket__caption font-ui text-small font-semibold leading-tight">{reward.caption}</span>
      </div>
      <div className="offer-ticket__body">
        <p className="m-0 line-clamp-2 font-ui text-small font-semibold leading-snug text-primary">{offer.playerMessage}</p>
        <p className="m-0 font-ui text-small text-secondary">{offerRule(offer, t)}</p>
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <OfferMarks offer={offer} now={now} />
        </div>
      </div>
    </article>
  );
}

/** Open offers first, used ones last. */
export function OfferTicketList({ offers, now }: { offers: CampaignOffer[]; now: number }) {
  const sorted = [...offers].sort((a, b) => Number(Boolean(a.used)) - Number(Boolean(b.used)));
  return (
    <div className="flex flex-col gap-2">
      {sorted.map((offer) => (
        <OfferTicket key={offer.id} offer={offer} now={now} />
      ))}
    </div>
  );
}
